import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Headphones, LogOut, Globe, ShieldCheck, Plus, Trash2, Pencil,
  Loader2, AlertCircle, CheckCircle2, ToggleLeft, ToggleRight,
  RefreshCw, X, Save,
  FileText, Megaphone, HelpCircle, Settings, Activity,
} from 'lucide-react'

// ─── API helper ───────────────────────────────────────────────────
function useAdminApi() {
  const navigate = useNavigate()
  const token = () => sessionStorage.getItem('adminToken') ?? ''

  async function req(method, path, body) {
    const res = await fetch(`/admin/api${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: body ? JSON.stringify(body) : undefined,
    })
    if (res.status === 401) { sessionStorage.removeItem('adminToken'); navigate('/admin'); return null }
    return res.ok ? res.json() : null
  }

  return { req }
}

// ─── Tab Button ───────────────────────────────────────────────────
function Tab({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
        active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

const CMS_SECTIONS = {
  posts: { title: 'Blog Posts', endpoint: '/posts', hint: 'Manage article SEO, author, taxonomy, images, content and draft/published status.' },
  announcements: { title: 'Announcements', endpoint: '/announcements', hint: 'Manage type, publishing window, and homepage/downloader placements.' },
  faqs: { title: 'FAQs', endpoint: '/faqs', hint: 'Manage ordered, categorized public questions and answers.' },
  content: { title: 'Site Content', endpoint: '/content', hint: 'Manage homepage hero/CTA and platform or static page content/SEO.' },
}

// ─── Downloader status controls ──────────────────────────────
const STATUS_STYLES = {
  enabled:     'bg-green-500/10 text-green-500',
  disabled:    'bg-muted text-muted-foreground',
  coming_soon: 'bg-amber-500/10 text-amber-500',
  maintenance: 'bg-orange-500/10 text-orange-500',
}

function DownloaderCard({ item, statuses, onSave, onToggle, busy }) {
  const [draft, setDraft] = useState(item)
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))
  const dirty = JSON.stringify(draft) !== JSON.stringify(item)

  return (
    <div className="bg-card border border-border/50 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-3">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[draft.status] ?? ''}`}>
          {String(draft.status).replace('_', ' ').toUpperCase()}
        </span>
        <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono text-muted-foreground">{draft.id}</code>
        <div className="flex-1" />
        <span className="text-[11px] text-muted-foreground">
          formats: {(item.formats ?? []).join(', ')}
        </span>
        <button
          onClick={() => onToggle(draft.id, !draft.enabled)}
          disabled={busy}
          title={draft.enabled ? 'Disable' : 'Enable'}
          className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
        >
          {draft.enabled ? <ToggleRight className="w-5 w-5 text-primary" /> : <ToggleLeft className="w-5 h-5 text-muted-foreground" />}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {[
          ['label', 'Display name', 60],
          ['description', 'Description', 300],
        ].map(([k, label, max]) => (
          <div key={k}>
            <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
            <input
              value={draft[k] ?? ''}
              maxLength={max}
              onChange={e => set(k, e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        ))}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Status</label>
          <select
            value={draft.status}
            onChange={e => set('status', e.target.value)}
            className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
          >
            {statuses.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Sort order</label>
          <input
            type="number"
            min={0}
            max={9999}
            value={draft.sortOrder ?? 0}
            onChange={e => set('sortOrder', Number(e.target.value))}
            className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        {[
          ['showInNavigation', 'Show in navigation'],
          ['showOnHomepage', 'Show on homepage'],
          ['indexable', 'Indexable (SEO)'],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!draft[k]} onChange={e => set(k, e.target.checked)} />
            {label}
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between pt-1">
        <p className="text-[11px] text-muted-foreground">
          Implementation is compiled into the server. Admin controls presentation and availability only.
        </p>
        <button
          onClick={() => onSave(draft.id, {
            label: draft.label,
            description: draft.description,
            status: draft.status,
            sortOrder: draft.sortOrder,
            showInNavigation: draft.showInNavigation,
            showOnHomepage: draft.showOnHomepage,
            indexable: draft.indexable,
          })}
          disabled={!dirty || busy}
          className="h-8 px-3 rounded-lg bg-primary text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          Save
        </button>
      </div>
    </div>
  )
}

function DownloadersPanel({ req, notify }) {
  const [state, setState] = useState({ downloaders: [], statuses: [] })
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const data = await req('GET', '/downloaders')
    if (data) setState(data)
  }, [req])
  useEffect(() => { load() }, [load])

  async function save(id, patch) {
    setBusy(true)
    const updated = await req('PUT', `/downloaders/${id}`, patch)
    setBusy(false)
    if (!updated) { notify('Update failed', false); return }
    setState(s => ({ ...s, downloaders: s.downloaders.map(d => (d.id === updated.id ? updated : d)) }))
    notify(`${updated.label} updated ✓`)
  }

  async function toggle(id, enable) {
    setBusy(true)
    const updated = await req('POST', `/downloaders/${id}/${enable ? 'enable' : 'disable'}`)
    setBusy(false)
    if (!updated) { notify('Update failed', false); return }
    setState(s => ({ ...s, downloaders: s.downloaders.map(d => (d.id === updated.id ? updated : d)) }))
    notify(`${updated.label} ${enable ? 'enabled' : 'disabled'} ✓`)
  }

  const enabled = state.downloaders.filter(d => d.enabled).length

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-bold text-lg">Downloaders</h2>
        <p className="text-sm text-muted-foreground">
          {state.downloaders.length} registered platforms · {enabled} enabled. Platforms are compiled into the
          server; these settings only control availability, ordering and display metadata.
        </p>
      </div>
      {state.downloaders.map(d => (
        <DownloaderCard
          key={d.id}
          item={d}
          statuses={state.statuses}
          onSave={save}
          onToggle={toggle}
          busy={busy}
        />
      ))}
    </section>
  )
}

function SettingsForm({ req, notify }) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(true)

  const load = useCallback(async () => {
    setBusy(true)
    const data = await req('GET', '/settings')
    if (data) setValue(JSON.stringify(data, null, 2))
    setBusy(false)
  }, [req])
  useEffect(() => { load() }, [load])

  async function save() {
    setBusy(true)
    let parsed
    try { parsed = JSON.parse(value) } catch { setBusy(false); notify('Enter valid JSON before saving', false); return }
    const saved = await req('PUT', '/settings', parsed)
    setBusy(false)
    if (!saved) { notify('Validation failed — check fields', false); return }
    setValue(JSON.stringify(saved, null, 2))
    notify('Settings saved ✓')
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-bold text-lg">Site Settings</h2>
        <p className="text-sm text-muted-foreground">
          Safe operational settings only. Engine secrets, environment values and filesystem paths are
          never editable here.
        </p>
      </div>
      {busy && value === '' ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : <>
        <textarea
          aria-label="Site settings JSON editor"
          value={value}
          onChange={e => setValue(e.target.value)}
          rows={24}
          spellCheck={false}
          className="w-full p-4 rounded-xl border border-border bg-card font-mono text-xs focus:outline-none focus:border-primary"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={load} className="h-9 px-4 rounded-lg border border-border text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />Reload
          </button>
          <button onClick={save} disabled={busy} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" />Validate &amp; Save
          </button>
        </div>
      </>}
    </section>
  )
}

function CmsEditor({ section, req, notify }) {
  const config = CMS_SECTIONS[section]
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(true)
  const load = useCallback(async () => {
    setBusy(true)
    const data = await req('GET', config.endpoint)
    if (data) setValue(JSON.stringify(data, null, 2))
    setBusy(false)
  }, [config.endpoint]) // eslint-disable-line
  useEffect(() => { load() }, [load])
  async function save() {
    try {
      const parsed = JSON.parse(value)
      const saved = await req('PUT', config.endpoint, parsed)
      if (!saved) { notify('Validation failed — check fields', false); return }
      setValue(JSON.stringify(saved, null, 2)); notify(`${config.title} saved ✓`)
    } catch { notify('Enter valid JSON before saving', false) }
  }
  return <section className="space-y-4">
    <div><h2 className="font-bold text-lg">{config.title}</h2><p className="text-sm text-muted-foreground">{config.hint}</p></div>
    {busy ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : <>
      <textarea aria-label={`${config.title} JSON editor`} value={value} onChange={event => setValue(event.target.value)}
        rows={24} spellCheck={false} className="w-full p-4 rounded-xl border border-border bg-card font-mono text-xs focus:outline-none focus:border-primary" />
      <div className="flex gap-2 justify-end">
        <button onClick={load} className="h-9 px-4 rounded-lg border border-border text-sm flex items-center gap-2"><RefreshCw className="w-4 h-4" />Reload</button>
        <button onClick={save} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold flex items-center gap-2"><Save className="w-4 h-4" />Validate &amp; Save</button>
      </div>
    </>}
  </section>
}

function SystemPanel({ req, notify }) {
  const [data, setData] = useState(null)
  const load = useCallback(() => req('GET', '/system').then(setData), []) // eslint-disable-line
  useEffect(() => { load() }, [load])
  async function cleanup() {
    const result = await req('POST', '/system/cleanup')
    if (result) { notify(`Removed ${result.removed} completed jobs`); load() }
  }
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-primary" />
  return <section className="space-y-5">
    <div><h2 className="font-bold text-lg">System Health</h2><p className="text-sm text-muted-foreground">Read-only process, provider and recent job status.</p></div>
    <div className="grid sm:grid-cols-3 gap-3">
      <div className="bg-card border border-border rounded-xl p-4"><b>Status</b><p className="text-green-500">{data.status}</p></div>
      <div className="bg-card border border-border rounded-xl p-4"><b>Uptime</b><p>{data.uptimeSeconds}s</p></div>
      <div className="bg-card border border-border rounded-xl p-4"><b>Memory</b><p>{Math.round(data.memory.rss / 1048576)} MB</p></div>
    </div>
    <div className="bg-card border border-border rounded-xl p-4"><b>Providers</b><p className="text-sm mt-2">{data.providers.map(item => `${item.name}: ${item.status}`).join(' · ')}</p></div>
    <div><div className="flex justify-between mb-2"><b>Recent jobs and errors</b><button onClick={cleanup} className="text-sm text-primary">Clean completed jobs</button></div>
      <div className="space-y-2">{data.jobs.length ? data.jobs.map(job => <div key={job.id} className="bg-card border border-border rounded-lg p-3 text-xs">
        <span className="font-mono">{job.id}</span> · {job.provider} · {job.status}{job.error && <p className="text-destructive mt-1">{job.error}</p>}
      </div>) : <p className="text-sm text-muted-foreground">No recent jobs.</p>}</div>
    </div>
  </section>
}

// ─── Page Form ────────────────────────────────────────────────────
function PageForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial ?? {
    slug: '', title: '', description: '', h1: '', h2: '',
    badge: '', subheading: '', content: '', published: false,
  })
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="bg-card border border-border/60 rounded-2xl p-6 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          ['slug',        'URL Slug (e.g. youtube-shorts-guide)',    'text'],
          ['title',       'SEO Title (<60 chars)',                   'text'],
          ['badge',       'Badge Text',                              'text'],
          ['h1',          'H1 Heading',                              'text'],
          ['h2',          'H2 Gradient Text',                        'text'],
          ['subheading',  'Subheading',                              'text'],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
            <input
              value={form[k]}
              onChange={e => set(k, e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
            />
          </div>
        ))}
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Meta Description (&lt;160 chars)</label>
        <input
          value={form.description}
          onChange={e => set('description', e.target.value)}
          className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
        />
      </div>
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Page Content (HTML or plain text)</label>
        <textarea
          value={form.content}
          onChange={e => set('content', e.target.value)}
          rows={6}
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono focus:outline-none focus:border-primary transition-colors resize-y"
          placeholder="<h2>About this page</h2><p>Content here...</p>"
        />
      </div>
      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <button type="button" onClick={() => set('published', !form.published)}>
            {form.published
              ? <ToggleRight className="w-8 h-8 text-primary" />
              : <ToggleLeft  className="w-8 h-8 text-muted-foreground" />}
          </button>
          {form.published ? 'Published' : 'Draft'}
        </label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="h-9 px-4 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors flex items-center gap-1.5">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button onClick={() => onSave(form)} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors flex items-center gap-1.5">
            <Save className="w-3.5 h-3.5" /> Save
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Dashboard ───────────────────────────────────────────────
export default function AdminDashboard() {
  const navigate   = useNavigate()
  const { req }    = useAdminApi()
  const [tab,      setTab]      = useState('pages')
  const [pages,    setPages]    = useState([])
  const [proxies,  setProxies]  = useState([])
  const [loading,  setLoading]  = useState(true)
  const [toast,    setToast]    = useState(null)
  const [editPage, setEditPage] = useState(null)   // null | 'new' | page object
  const [proxyUrl, setProxyUrl] = useState('')
  const [proxyLbl, setProxyLbl] = useState('')

  const notify = (msg, ok = true) => {
    setToast({ msg, ok })
    setTimeout(() => setToast(null), 3000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    const [p, pr] = await Promise.all([req('GET', '/pages'), req('GET', '/proxies')])
    if (p)  setPages(p)
    if (pr) setProxies(pr)
    setLoading(false)
  }, []) // eslint-disable-line

  useEffect(() => {
    // Verify token on mount
    req('GET', '/verify').then(r => { if (!r) return })
    load()
  }, []) // eslint-disable-line

  // ── Pages handlers
  async function savePage(form) {
    if (editPage === 'new') {
      const created = await req('POST', '/pages', form)
      if (created) { notify('Page created ✓'); setPages(p => [...p, created]); setEditPage(null) }
      else notify('Failed to create page', false)
    } else {
      const updated = await req('PUT', `/pages/${editPage.slug}`, form)
      if (updated) { notify('Page saved ✓'); setPages(p => p.map(x => x.slug === updated.slug ? updated : x)); setEditPage(null) }
      else notify('Failed to save page', false)
    }
  }

  async function deletePage(slug) {
    if (!confirm(`Delete page /${slug}?`)) return
    await req('DELETE', `/pages/${slug}`)
    setPages(p => p.filter(x => x.slug !== slug))
    notify('Page deleted')
  }

  async function togglePublished(page) {
    const updated = await req('PUT', `/pages/${page.slug}`, { published: !page.published })
    if (updated) setPages(p => p.map(x => x.slug === updated.slug ? updated : x))
  }

  // ── Proxy handlers
  async function addProxy() {
    if (!proxyUrl.trim()) return
    const p = await req('POST', '/proxies', { url: proxyUrl.trim(), label: proxyLbl.trim() || proxyUrl.trim() })
    if (p) { setProxies(ps => [...ps, p]); setProxyUrl(''); setProxyLbl(''); notify('Proxy added ✓') }
    else notify('Failed to add proxy', false)
  }

  async function toggleProxy(proxy) {
    const updated = await req('PATCH', `/proxies/${proxy.id}`, { active: !proxy.active })
    if (updated) setProxies(ps => ps.map(p => p.id === updated.id ? updated : p))
  }

  async function deleteProxy(id) {
    await req('DELETE', `/proxies/${id}`)
    setProxies(ps => ps.filter(p => p.id !== id))
    notify('Proxy removed')
  }

  function logout() {
    sessionStorage.removeItem('adminToken')
    navigate('/admin')
  }

  return (
    <div className="min-h-screen bg-background">

      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Headphones className="w-5 h-5 text-primary" />
            <span className="font-bold">FastAudio</span>
            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Admin</span>
          </div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* Tabs */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Tab active={tab === 'pages'}   onClick={() => setTab('pages')}   icon={Globe}        label="SEO Pages" />
          <Tab active={tab === 'posts'} onClick={() => setTab('posts')} icon={FileText} label="Blog" />
          <Tab active={tab === 'announcements'} onClick={() => setTab('announcements')} icon={Megaphone} label="Announcements" />
          <Tab active={tab === 'faqs'} onClick={() => setTab('faqs')} icon={HelpCircle} label="FAQs" />
          <Tab active={tab === 'content'} onClick={() => setTab('content')} icon={Pencil} label="Content" />
          <Tab active={tab === 'downloaders'} onClick={() => setTab('downloaders')} icon={Globe} label="Downloaders" />
          <Tab active={tab === 'settings'} onClick={() => setTab('settings')} icon={Settings} label="Site Settings" />
          <Tab active={tab === 'proxies'} onClick={() => setTab('proxies')} icon={ShieldCheck}  label="Proxies" />
          <Tab active={tab === 'system'} onClick={() => setTab('system')} icon={Activity} label="System" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (

          /* ── PAGES TAB ─────────────────────────────────────── */
          tab === 'pages' ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bold text-lg">Dynamic SEO Pages</h2>
                  <p className="text-sm text-muted-foreground">{pages.length} pages — live at fastaudio.cc/p/[slug]</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={load} className="h-9 px-3 rounded-lg border border-border text-sm hover:bg-muted transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setEditPage('new')} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Add Page
                  </button>
                </div>
              </div>

              {editPage && (
                <PageForm
                  initial={editPage === 'new' ? null : editPage}
                  onSave={savePage}
                  onCancel={() => setEditPage(null)}
                />
              )}

              {pages.length === 0 && !editPage ? (
                <div className="text-center py-16 text-muted-foreground">
                  <Globe className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No dynamic pages yet</p>
                  <p className="text-sm mt-1">Click "Add Page" to create your first SEO page.</p>
                </div>
              ) : (
                pages.map(page => (
                  <div key={page.slug} className="bg-card border border-border/50 rounded-xl p-4 flex items-start gap-4 hover:border-primary/30 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-xs bg-muted px-2 py-0.5 rounded font-mono text-muted-foreground">/p/{page.slug}</code>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${page.published ? 'bg-green-500/10 text-green-500' : 'bg-muted text-muted-foreground'}`}>
                          {page.published ? 'LIVE' : 'DRAFT'}
                        </span>
                      </div>
                      <p className="font-semibold text-sm truncate">{page.title}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{page.description}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => togglePublished(page)} title={page.published ? 'Unpublish' : 'Publish'}
                        className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                        {page.published ? <ToggleRight className="w-4 h-4 text-primary" /> : <ToggleLeft className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setEditPage(page)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deletePage(page.slug)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

          ) : tab === 'downloaders' ? (
            <DownloadersPanel req={req} notify={notify} />
          ) : tab === 'settings' ? (
            <SettingsForm req={req} notify={notify} />
          ) : tab === 'system' ? (
            <SystemPanel req={req} notify={notify} />
          ) : CMS_SECTIONS[tab] ? (
            <CmsEditor section={tab} req={req} notify={notify} />
          ) : (

            /* ── PROXIES TAB ────────────────────────────────── */
            <div className="space-y-4">
              <div className="mb-4">
                <h2 className="font-bold text-lg">Proxy Management</h2>
                <p className="text-sm text-muted-foreground">
                  Configuration inventory for authorized outbound infrastructure. These records are not connected to downloader traffic yet, and credentials must remain in deployment secrets.
                </p>
              </div>

              {/* Add proxy form */}
              <div className="bg-card border border-border/50 rounded-xl p-4 flex gap-3 flex-wrap">
                <input
                  value={proxyUrl}
                  onChange={e => setProxyUrl(e.target.value)}
                  placeholder="Credential-free proxy URL (e.g. https://proxy.example.com:8443)"
                  className="flex-1 min-w-48 h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
                />
                <input
                  value={proxyLbl}
                  onChange={e => setProxyLbl(e.target.value)}
                  placeholder="Label (optional)"
                  className="w-40 h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
                />
                <button onClick={addProxy} disabled={!proxyUrl.trim()} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Add Proxy
                </button>
              </div>

              {proxies.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No proxies configured</p>
                  <p className="text-sm mt-1">Add a proxy above to improve download reliability.</p>
                </div>
              ) : (
                proxies.map(proxy => (
                  <div key={proxy.id} className="bg-card border border-border/50 rounded-xl p-4 flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${proxy.active ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{proxy.label}</p>
                      <code className="text-xs text-muted-foreground font-mono truncate block">{proxy.host || 'Credential protected'}</code>
                      <p className="text-[11px] text-muted-foreground">Health: {proxy.status || 'unknown'} · failures: {proxy.failureCount || 0}{proxy.lastCheckedAt ? ` · checked ${new Date(proxy.lastCheckedAt).toLocaleString()}` : ''}</p>
                    </div>
                    <button onClick={() => toggleProxy(proxy)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground">
                      {proxy.active ? <ToggleRight className="w-5 h-5 text-primary" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button onClick={() => deleteProxy(proxy.id)} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          )
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-lg transition-all ${toast.ok ? 'bg-green-500 text-white' : 'bg-destructive text-white'}`}>
          {toast.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  )
}
